import { triggerWalletConnect } from '@/features/analytics'
import { Form } from '@/ui/atoms/form/Form'
import { Button } from '@/ui/atoms/button/Button'
import { Typography } from '@/ui/atoms/typography/Typography'
import { ControlledMultiSelectorAssetInput } from '@/ui/organisms/multi-selector/ControlledMultiSelectorAssetInput'
import { Panel } from '@/ui/atoms/panel/Panel'

import { AssetSelector } from '@/ui/molecules/asset-selector/AssetSelector'
import { UseFormReturn } from 'react-hook-form'
import { FormFieldsForDialog, GsmMintState } from '../../logic/types'
import { MintFormSchema } from '../../logic/validators'

interface MintFormProps {
  form: UseFormReturn<MintFormSchema>
  assetsToMintFields: FormFieldsForDialog
  gsmMint: () => void
  actionsEnabled: boolean
  gsmMintState: GsmMintState
  guestMode: boolean
  openConnectModal: () => void
}

export function MintForm({
  form,
  gsmMint,
  actionsEnabled,
  gsmMintState,
  assetsToMintFields,
  guestMode,
  openConnectModal,
}: MintFormProps) {
  const {
    selectedFromAsset,
    selectedToAsset,
    fromAssets,
    toAssets,
    changeFromAsset,
    changeToAsset,
    changeFromValue,
    maxValue,
    maxSelectedFieldName,
    onSetMax,
  } = assetsToMintFields

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(() => {})}>
          <div className="relative z-10">
            <Panel.Wrapper className="mt-2 p-4 pb-5">
              <Typography variant="h4" className="py-3 font-semibold">
                Mint with
              </Typography>

              <div>
                <div className="mt-2 flex flex-row items-start gap-2">
                  <ControlledMultiSelectorAssetInput
                    fieldName="fromAsset.value"
                    control={form.control}
                    disabled={gsmMintState.status === 'in-progress'}
                    token={selectedFromAsset.token}
                    max={maxValue}
                    balance={selectedFromAsset.balance}
                    maxSelectedFieldName={maxSelectedFieldName}
                    customOnChange={changeFromValue}
                    onSetMax={onSetMax}
                  />

                  <AssetSelector
                    assets={fromAssets}
                    selectedAsset={selectedFromAsset.token}
                    setSelectedAsset={changeFromAsset}
                    disabled={gsmMintState.status === 'in-progress'}
                  />
                </div>
              </div>
            </Panel.Wrapper>
          </div>

          <div className="relative z-10">
            <Panel.Wrapper className="mt-2 p-4 pb-5">
              <Typography variant="h4" className="py-3 font-semibold">
                Receive
              </Typography>

              <div>
                <div className="mt-2 flex flex-row items-start gap-2">
                  <ControlledMultiSelectorAssetInput
                    fieldName="toAsset.value"
                    readOnly
                    disabled
                    control={form.control}
                    token={selectedToAsset.token}
                    balance={selectedToAsset.balance}
                  />

                  <AssetSelector
                    assets={toAssets}
                    selectedAsset={selectedToAsset.token}
                    setSelectedAsset={changeToAsset}
                    disabled
                  />
                </div>
              </div>
            </Panel.Wrapper>
          </div>

          <div className="mt-4">
            <div className="relative">
              {guestMode ? (
                <Button
                  className="w-full"
                  onClick={() => void triggerWalletConnect(openConnectModal, 'usdxl_mint_form')}
                >
                  Connect Wallet
                </Button>
              ) : (
                <Button
                  className={'w-full rounded-lg'}
                  onClick={gsmMint}
                  disabled={!actionsEnabled || gsmMintState.status === 'in-progress'}
                >
                  {(() => {
                    switch (true) {
                      case gsmMintState.status === 'in-progress':
                        return gsmMintState.message
                      default:
                        return `Mint ${selectedToAsset.token.symbol}`
                    }
                  })()}
                </Button>
              )}
            </div>
          </div>
        </form>
      </Form>

      {gsmMintState.status === 'error' && (
        <div className="mt-4">
          <Typography variant="prompt" className="text-red-300 text-sm">
            {gsmMintState.message}
          </Typography>
        </div>
      )}
    </>
  )
}
